import { getCountry } from '@pkg/utilities/i18n';
import {
  injectJsonLdScript,
  matchPathSegment,
  useJsonLdShared,
  type PageSeoData,
} from './useJsonLdShared';

/** Product pages that expose Service JSON-LD (exact slug match, no nested paths). */
const PRODUCT_SERVICES: Record<string, { serviceType: string; defaultTitle: string }> = {
  'surplus-food-marketplace': {
    serviceType: 'Surprise Bags',
    defaultTitle: 'Surprise Bags',
  },
  platform: {
    serviceType: 'Too Good To Go Platform',
    defaultTitle: 'Too Good To Go Platform',
  },
  'look-smell-taste': {
    serviceType: 'Look-Smell-Taste',
    defaultTitle: 'Look-Smell-Taste',
  },
  'surplus-food-parcels': {
    serviceType: 'Parcels',
    defaultTitle: 'Parcels',
  },
};

interface ServiceJsonLdOptions {
  pageData: PageSeoData;
}

/**
 * Builds and injects Service JSON-LD for product pages.
 * Uses existing SEO title/description; serviceType is mapped by slug (no Sanity fields).
 */
export function useServiceJsonLd(options: ServiceJsonLdOptions) {
  const {
    route,
    localeValue,
    localePath,
    baseUrl,
    organizationId,
    seoFromPageData,
  } = useJsonLdShared();

  const productSlug = computed(() => {
    for (const slug of Object.keys(PRODUCT_SERVICES)) {
      if (matchPathSegment(route.path, slug)) {
        return slug;
      }
    }
    return null;
  });

  const jsonLd = computed(() => {
    const slug = productSlug.value;
    if (!slug) {
      return null;
    }

    const product = PRODUCT_SERVICES[slug];
    const siteBase = baseUrl.value;
    const pageUrl = `${siteBase}${localePath(`/${slug}`)}`;
    const { name, description } = seoFromPageData(options.pageData, product.defaultTitle);

    return {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${pageUrl}#service`,
      name,
      ...(description && { description }),
      serviceType: product.serviceType,
      url: pageUrl,
      provider: { '@id': organizationId.value },
      areaServed: {
        '@type': 'Country',
        name: getCountry(localeValue.value),
      },
    };
  });

  injectJsonLdScript('service-jsonld', jsonLd);
}
